import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Card, Chip } from '@structural-climbing/ui'

interface Props<T> {
  title: ReactNode
  /** Shown as a chip beside the title. */
  count?: number
  items: readonly T[]
  renderItem: (item: T) => ReactNode
  getKey: (item: T) => string
  /** Rows shown before "+ N more". Omit to show every row. */
  initialCount?: number
  emptyText?: ReactNode
  /** Controls on the right of the header. */
  headerEnd?: ReactNode
}

/**
 * A white card with a titled header and divided rows: the list shape every gym
 * page uses. Long lists show the first few with "+ N more" to open the rest.
 */
export function ListCard<T>({
  title,
  count,
  items,
  renderItem,
  getKey,
  initialCount,
  emptyText,
  headerEnd,
}: Props<T>) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const limit = initialCount !== undefined && !expanded ? initialCount : items.length
  const shown = items.slice(0, limit)
  const hidden = items.length - shown.length

  return (
    <Card shadow="sm">
      <div className="flex items-center gap-2 border-b border-divider px-4 py-3">
        <h2 className="flex-1 text-base font-semibold">{title}</h2>
        {headerEnd}
        {count !== undefined && (
          <Chip size="sm" variant="flat" color="primary">
            {count}
          </Chip>
        )}
      </div>
      {items.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-foreground/50">{emptyText}</p>
      ) : (
        <div className="divide-y divide-divider">
          {shown.map((item) => (
            <div key={getKey(item)} className="px-4 py-3">
              {renderItem(item)}
            </div>
          ))}
          {(hidden > 0 || expanded) && initialCount !== undefined && (
            <div className="px-2 py-1.5">
              <Button size="sm" variant="light" onPress={() => setExpanded((v) => !v)}>
                {expanded ? t('common.showLess') : t('common.showMore', { count: hidden })}
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  )
}
